import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontSize: 10,
    lineHeight: 1.3,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 5,
  },
  headerImage: {
    width: '100%',
    maxHeight: 120,
    objectFit: 'contain',
    marginBottom: 5,
  },
  topDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  refNo: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  date: {
    fontSize: 10,
  },
  toSection: {
    marginBottom: 10,
  },
  toLabel: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  toName: {
    fontSize: 10,
    fontWeight: 'bold',
    marginTop: 2,
  },
  subject: {
    marginBottom: 10,
  },
  subjectLabel: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  subjectText: {
    fontSize: 10,
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  reference: {
    marginBottom: 10,
  },
  referenceLabel: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  referenceText: {
    fontSize: 10,
    fontStyle: 'italic',
  },
  offerText: {
    marginBottom: 10,
    fontSize: 10,
  },
  ourOffer: {
    marginBottom: 15,
  },
  ourOfferLabel: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  ourOfferText: {
    fontSize: 10,
  },
  table: {
    width: '100%',
    border: '1pt solid #000000',
    borderCollapse: 'collapse',
    marginBottom: 10,
  },
  tableHeader: {
    backgroundColor: '#E5E7EB',
    flexDirection: 'row',
  },
  tableHeaderCell: {
    border: '1pt solid #000000',
    padding: 8,
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCell: {
    border: '1pt solid #000000',
    padding: 8,
    fontSize: 9,
  },
  tableCellCenter: {
    textAlign: 'center',
  },
  tableCellRight: {
    textAlign: 'right',
  },
  srNoCell: {
    width: '8%',
  },
  descriptionCell: {
    width: '52%',
  },
  quantityCell: {
    width: '12%',
  },
  rateCell: {
    width: '14%',
  },
  totalCell: {
    width: '14%',
  },
  amountInWords: {
    marginBottom: 15,
    fontSize: 10,
    fontWeight: 'bold',
  },
  amountLabel: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  amountText: {
    fontSize: 10,
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  termsSection: {
    marginBottom: 15,
  },
  termsTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    textDecoration: 'underline',
    marginBottom: 8,
  },
  termsList: {
    paddingLeft: 0,
  },
  termItem: {
    fontSize: 8,
    marginBottom: 3,
    lineHeight: 1.2,
  },
  closingText: {
    marginBottom: 10,
    fontSize: 10,
  },
  signature: {
    marginBottom: 10,
  },
  salutation: {
    fontSize: 10,
    marginBottom: 5,
  },
  signatureText: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  watermark: {
    position: 'absolute',
    bottom: 50,
    right: 20,
    fontSize: 40,
    color: '#E5E7EB',
    opacity: 0.1,
    transform: 'rotate(90deg)',
    transformOrigin: 'center',
  },
});

interface QuotationData {
  refNo: string;
  date: string;
  to: string;
  subject: string;
  yourRef: string;
  offerText: string;
  ourOffer: string;
  amountInWords: string;
  termsAndConditions: string[];
  closingText: string;
  salutation: string;
  signature: string;
}

interface QuotationService {
  srNo: string;
  description: string;
  quantity: string;
  rate: string;
  totalAmount: string;
}

interface QuotationPDFProps {
  quotationData: QuotationData;
  services: QuotationService[];
}

const QuotationPDF: React.FC<QuotationPDFProps> = ({ quotationData, services }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Image */}
        <View style={styles.header}>
          <Image 
            src="/cpheader.png" 
            style={styles.headerImage}
          />
        </View>

        {/* Top Details */}
        <View style={styles.topDetails}>
          <View style={{ flex: 1 }}>
            <Text style={styles.refNo}>Ref No: {quotationData.refNo}</Text>
          </View>
          <View>
            <Text style={styles.date}>Date: {quotationData.date}</Text>
          </View>
        </View>

        {/* To Section */}
        <View style={styles.toSection}>
          <Text style={styles.toLabel}>To,</Text>
          <Text style={styles.toName}>{quotationData.to}</Text>
        </View>

        {/* Subject */}
        <View style={styles.subject}>
          <Text style={styles.subjectLabel}>Subject: </Text>
          <Text style={styles.subjectText}>{quotationData.subject}</Text>
        </View>

        {/* Reference */}
        <View style={styles.reference}>
          <Text style={styles.referenceLabel}>Reference: </Text>
          <Text style={styles.referenceText}>{quotationData.yourRef}</Text>
        </View>

        {/* Offer Text */}
        <View style={styles.offerText}>
          <Text>{quotationData.offerText}</Text>
        </View>

        {/* Our Offer */}
        <View style={styles.ourOffer}>
          <Text style={styles.ourOfferLabel}>A) OUR OFFER :- </Text>
          <Text style={styles.ourOfferText}>{quotationData.ourOffer}</Text>
        </View>

        {/* Services Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.tableHeaderCell, styles.srNoCell]}>
              <Text>Sr No.</Text>
            </View>
            <View style={[styles.tableHeaderCell, styles.descriptionCell]}>
              <Text>Description</Text>
            </View>
            <View style={[styles.tableHeaderCell, styles.quantityCell]}>
              <Text>Quantity</Text>
            </View>
            <View style={[styles.tableHeaderCell, styles.rateCell]}>
              <Text>Rate</Text>
            </View>
            <View style={[styles.tableHeaderCell, styles.totalCell]}>
              <Text>Total Amount</Text>
            </View>
          </View>

          {/* Table Rows */}
          {services.map((service, index) => (
            <View key={index} style={styles.tableRow}>
              <View style={[styles.tableCell, styles.srNoCell, styles.tableCellCenter]}>
                <Text>{service.srNo}</Text>
              </View>
              <View style={[styles.tableCell, styles.descriptionCell]}>
                <Text>{service.description}</Text>
              </View>
              <View style={[styles.tableCell, styles.quantityCell, styles.tableCellCenter]}>
                <Text>{service.quantity}</Text>
              </View>
              <View style={[styles.tableCell, styles.rateCell, styles.tableCellRight]}>
                <Text>{service.rate}</Text>
              </View>
              <View style={[styles.tableCell, styles.totalCell, styles.tableCellRight]}>
                <Text>{service.totalAmount}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Amount in Words */}
        <View style={styles.amountInWords}>
          <Text style={styles.amountLabel}>Amount in Word:- </Text>
          <Text style={styles.amountText}>{quotationData.amountInWords}</Text>
        </View>

        {/* Terms and Conditions */}
        <View style={styles.termsSection}>
          <Text style={styles.termsTitle}>TERMS/CONDITIONS:-</Text>
          <View style={styles.termsList}>
            {quotationData.termsAndConditions.map((term, index) => (
              <Text key={index} style={styles.termItem}>
                {term}
              </Text>
            ))}
          </View>
        </View>

        {/* Closing Text */}
        <View style={styles.closingText}>
          <Text>{quotationData.closingText}</Text>
        </View>

        {/* Signature */}
        <View style={styles.signature}>
          <Text style={styles.salutation}>{quotationData.salutation}</Text>
          <Text style={styles.signatureText}>{quotationData.signature}</Text>
        </View>

        {/* Watermark */}
        <View style={styles.watermark}>
          <Text>AVIS</Text>
        </View>
      </Page>
    </Document>
  );
};

export default QuotationPDF;
